import { IconButton, IconButtonProps } from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";
import {
  IoStopCircleSharp,
  IoPlayCircleSharp,
  IoStopCircleOutline,
  IoPlayCircleOutline,
} from "react-icons/io5";
import { ConversationMessage } from "../types/conversation";
import { logger } from "../utils/logger";
import { VoiceControlContext } from "./chat/hooks/use-voice-control";

interface Props extends Omit<IconButtonProps, "aria-label"> {
  message: ConversationMessage;
  onStartCb?: () => void;
  inverted?: boolean;
  overrideVoiceId?: string;
}

export default function ReadButton({
  message,
  onStartCb,
  inverted,
  overrideVoiceId,
  ...iconButtonProps
}: Props) {
  const voiceControl = useContext(VoiceControlContext);
  const isLoadingForSomeMessage = !!voiceControl.isLoadingAudioForMessageId;

  const isLoadingMessageAudio = voiceControl.isLoadingAudioForMessageId === message.id;
  const isReadingThis = voiceControl.readAloudMessage?.id === message.id;
  const [toggleIsLoading, setToggleIsLoading] = useState(false);

  useEffect(() => {
    if (isReadingThis && toggleIsLoading) {
      setToggleIsLoading(false);
    }
  }, [isReadingThis, toggleIsLoading, setToggleIsLoading]);

  const onPlayPause = async (isReadingThis: boolean, message: ConversationMessage) => {
    setToggleIsLoading(true);
    onStartCb?.();

    try {
      if (isReadingThis) {
        await voiceControl.stopAiSpeak();
        return;
      }

      await voiceControl.startAiSpeak(message, false, overrideVoiceId);
    } catch (err: any) {
      setToggleIsLoading(false);
      logger.error("Error in voiceControl.startAiSpeak", err);
    }
  };

  const icon = isReadingThis ? <IoStopCircleSharp /> : <IoPlayCircleSharp />;
  const iconInverted = isReadingThis ? <IoStopCircleOutline /> : <IoPlayCircleOutline />;

  return (
    <IconButton
      aria-label="Play"
      variant={"transparent"}
      color={"special.chatIconColor"}
      size={"sm"}
      fontSize={"15px"}
      icon={inverted ? iconInverted : icon}
      onClick={() => {
        if (!isLoadingForSomeMessage) {
          onPlayPause(isReadingThis, message);
        }
      }}
      cursor={isLoadingForSomeMessage ? "wait" : "pointer"}
      isLoading={isLoadingMessageAudio || toggleIsLoading}
      {...iconButtonProps}
    />
  );
}
